import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import {
  SessionContext,
  completeHandOff,
  expireLoops,
  releaseHandOff,
  type SessionContextValue,
} from "./context";

/**
 * The browser owns no OAuth credential. It asks the same-origin BFF whether its
 * opaque HttpOnly session cookie is valid; the BFF completes Authorization Code
 * + PKCE, rotates refresh tokens and proxies Control requests with the access
 * token. See Caravan decision 0019.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionContextValue["status"]>("loading");

  const check = useCallback(async () => {
    try {
      const response = await fetch("/auth/session", {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      const result = response.ok
        ? ((await response.json()) as { authenticated?: boolean })
        : { authenticated: false };
      if (result.authenticated) {
        // The hand-off worked, so the next sign-out gets an automatic one too.
        // Without this the fallback screen would be sticky for the tab's life.
        completeHandOff(sessionStorage);
      }
      setStatus(result.authenticated ? "signed-in" : "signed-out");
    } catch {
      setStatus("signed-out");
    }
  }, []);

  useEffect(() => {
    // One-time migration from the browser-only client. Leaving its refresh
    // token behind would preserve the exact credential exposure the BFF was
    // introduced to remove, even though new code no longer reads it.
    localStorage.removeItem("mw.refresh");
    sessionStorage.removeItem("mw.pkce");
    void check();
  }, [check]);

  const signIn = useCallback((options?: { create?: boolean }) => {
    window.location.assign(options?.create ? "/auth/login?create=1" : "/auth/login");
    return Promise.resolve();
  }, []);

  const [stuck, setStuck] = useState(false);

  const expire = useCallback(() => {
    setStuck(expireLoops(sessionStorage));
    setStatus("signed-out");
  }, []);

  const signOut = useCallback(async () => {
    // Not "signed-out": that state hands off to the sign-in screen, and doing
    // so now would race the request below and sign the person back in.
    setStatus("signing-out");
    // Give the hand-off back. Signing out is not a failed sign-in, and without
    // this the next visit to the console meets the fallback screen — "that
    // sign-in did not complete" — which is both wrong and alarming when what
    // actually happened is that somebody asked to leave.
    releaseHandOff(sessionStorage);
    let next = "/";
    try {
      const response = await fetch("/auth/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: { "x-mw-csrf": "1" },
      });
      if (response.ok) {
        const answer = (await response.json()) as { next?: string };
        if (answer.next) next = answer.next;
      }
    } catch {
      // The network is gone. Reloading is still the honest thing to do — the
      // local cookie may or may not have been cleared, and the next page load
      // asks the only authority that can say.
    }
    // Away from this application either way, so no stale screen is left behind
    // holding data belonging to somebody who has asked to leave.
    window.location.assign(next);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, signIn, expire, stuck, signOut }),
    [status, signIn, expire, stuck, signOut],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
